/**
 * @module fieldTransforms
 *
 * Reusable value coercion functions for file upload processing.
 *
 * Provides composable transforms for converting raw spreadsheet values
 * (from Excel or CSV) into typed application values. These transforms handle
 * the messy reality of user-uploaded data: Excel serial dates, comma-formatted
 * numbers, boolean-like strings ("Yes"/"No"), N/A variants, and more.
 *
 * **Architecture role:** This module sits between parsing (parseFileData) and
 * validation (validators.js). The upload pipeline applies transforms to each
 * cell value BEFORE validation runs, so validators see clean typed values.
 *
 * **Usage patterns:**
 * 1. **Automatic via `buildFieldTransforms`** -- pass your fieldDefs and get a
 *    transform map keyed by field name. Used by `useUploadConfig` internally.
 * 2. **Manual composition** -- use `composeTransforms` to chain transforms for
 *    custom fields that need special handling beyond date/numeric/string.
 * 3. **Direct use** -- call `coerceDate` or `coerceNumeric` directly when you
 *    need to transform a single value outside the upload pipeline.
 *
 * @example <caption>Building transforms from fieldDefs (most common)</caption>
 * import { buildFieldTransforms } from '@1823-partners/core';
 * import creFields from '../data/creFields.json';
 *
 * // Automatically creates coerceDate for date fields, coerceNumeric for numeric fields
 * const transforms = buildFieldTransforms(creFields);
 * // transforms.MATURITY_DATE = composeTransforms(normalizeNA, coerceDate)
 * // transforms.SERVICER_NUMBER = composeTransforms(normalizeNA, coerceNumeric)
 * // transforms.LOAN_NAME = normalizeNA
 *
 * @example <caption>With per-field overrides</caption>
 * import { buildFieldTransforms, coerceNumeric, composeTransforms } from '@1823-partners/core';
 *
 * const transforms = buildFieldTransforms(fieldDefs, {
 *   // Custom transform for a field that needs special handling
 *   RATING: (value) => value?.toString().toUpperCase().trim() || null,
 *   // Chain normalizeNA with a custom numeric parser that handles '%' suffix
 *   YIELD: composeTransforms(
 *     (v) => typeof v === 'string' ? v.replace('%', '') : v,
 *     coerceNumeric,
 *   ),
 * });
 */

const EXCEL_SERIAL_MIN = 1;
const EXCEL_SERIAL_MAX = 200000;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const NA_RE = /^n\/?a$/i;

/**
 * Coerce a value to a `YYYY-MM-DD` date string.
 *
 * Handles all common date representations found in Excel/CSV uploads:
 *
 * | Input type | Example input | Output |
 * |---|---|---|
 * | Excel serial number (1-200000) | `44927` | `'2023-01-01'` |
 * | JavaScript Date object | `new Date('2023-01-01')` | `'2023-01-01'` |
 * | ISO YYYY-MM-DD string | `'2023-01-01'` | `'2023-01-01'` (returned as-is) |
 * | Other parseable date string | `'Jan 1, 2023'`, `'1/1/2023'` | `'2023-01-01'` |
 * | Invalid Date object | `new Date('invalid')` | `null` |
 * | Unparseable string | `'not a date'` | `null` |
 * | null / undefined / `''` | | `null` |
 *
 * **Excel serial number handling:** Excel stores dates as the number of days
 * since 1899-12-30 (the "1900 date system"). Serial number `1` = 1900-01-01.
 * Values in the range [1, 200000] are treated as serial numbers. This covers
 * dates from 1900-01-01 through approximately the year 2447.
 *
 * **Why `cellDates: true` matters:** When `parseFileData` is called with
 * `cellDates: true` (the default), the xlsx library converts date cells to
 * JavaScript Date objects, so this function receives Date objects. When
 * `cellDates: false`, date cells arrive as Excel serial numbers instead.
 * Either way, `coerceDate` handles both.
 *
 * @param {*} value - The raw cell value to coerce
 * @returns {string|null} Date string in `YYYY-MM-DD` format, or `null` if the
 *   value cannot be interpreted as a date
 *
 * @example
 * coerceDate(44927);                    // '2023-01-01' (Excel serial)
 * coerceDate(new Date('2023-06-15'));   // '2023-06-15' (Date object)
 * coerceDate('2023-06-15');             // '2023-06-15' (already formatted)
 * coerceDate('Jun 15, 2023');           // '2023-06-15' (parseable string)
 * coerceDate('');                       // null
 * coerceDate(null);                     // null
 * coerceDate('not a date');             // null
 */
export function coerceDate(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // Excel serial number
  if (typeof value === 'number' && value >= EXCEL_SERIAL_MIN && value <= EXCEL_SERIAL_MAX) {
    const d = new Date(Date.UTC(1899, 11, 30 + Math.floor(value)));
    return d.toISOString().split('T')[0];
  }

  // JavaScript Date object
  if (value instanceof Date) {
    if (isNaN(value.getTime())) {
      return null;
    }
    return value.toISOString().split('T')[0];
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (trimmed === '') {
      return null;
    }

    // Already in YYYY-MM-DD format
    if (DATE_RE.test(trimmed)) {
      return trimmed;
    }

    // Try to parse as a date string
    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
  }

  return null;
}

/**
 * Coerce a value to a number.
 *
 * Handles all common numeric representations found in Excel/CSV uploads:
 *
 * | Input type | Example input | Output |
 * |---|---|---|
 * | Number | `42`, `3.14`, `0` | Returned as-is |
 * | Boolean-like "yes" | `'Yes'`, `'Y'`, `'yes'`, `'y'` | `1` |
 * | Boolean-like "no" | `'No'`, `'N'`, `'no'`, `'n'` | `0` |
 * | Currency-formatted | `'$1,234.56'` | `1234.56` |
 * | Comma-formatted | `'1,000,000'` | `1000000` |
 * | Leading number with text | `'3 = full I/O'` | `3` |
 * | Pure numeric string | `'42.5'` | `42.5` |
 * | Negative values | `'-3.14'`, `'-$1,000'` | `-3.14`, `-1000` |
 * | null / undefined | | `null` |
 * | Empty string / whitespace | `''`, `'  '` | `null` |
 * | Unparseable text | `'hello'` | `null` |
 *
 * **Coercion priority:** The function tries these strategies in order:
 * 1. If already a number, return it
 * 2. Check for boolean-like strings (Yes/Y/No/N)
 * 3. Strip `$`, `,`, whitespace and parse as float (only if entire stripped string is numeric)
 * 4. Extract leading number from mixed text (e.g., `'3 = full I/O'` -> `3`)
 * 5. Return null if nothing works
 *
 * @param {*} value - The raw cell value to coerce
 * @returns {number|null} The coerced number, or `null` if the value cannot be
 *   interpreted as a number
 *
 * @example
 * coerceNumeric(42);             // 42
 * coerceNumeric('$1,234.56');    // 1234.56
 * coerceNumeric('Yes');          // 1
 * coerceNumeric('No');           // 0
 * coerceNumeric('3 = full I/O'); // 3
 * coerceNumeric('');             // null
 * coerceNumeric('hello');        // null
 */
export function coerceNumeric(value) {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (trimmed === '') {
      return null;
    }

    // Boolean-like strings
    if (/^(yes|y)$/i.test(trimmed)) {
      return 1;
    }
    if (/^(no|n)$/i.test(trimmed)) {
      return 0;
    }

    // Strip currency/formatting chars and attempt direct parse
    const stripped = trimmed.replace(/[$,\s]/g, '');
    const direct = parseFloat(stripped);
    if (!isNaN(direct) && stripped !== '') {
      // Make sure the whole stripped string is numeric (no trailing text)
      if (/^-?[\d.]+$/.test(stripped)) {
        return direct;
      }
    }

    // Extract leading number from strings like "3 = full I/O"
    const match = trimmed.match(/^(-?[\d,.]+)/);
    if (match) {
      const leading = parseFloat(match[1].replace(/,/g, ''));
      if (!isNaN(leading)) {
        return leading;
      }
    }

    return null;
  }

  return null;
}

/**
 * Normalize N/A variants to null.
 *
 * Matches (case-insensitive): `N/A`, `n/a`, `NA`, `na`, `N/a`, `n/A`, etc.
 * Returns `null` if the value matches any N/A variant; otherwise returns the
 * original value unchanged. This is typically the first transform in a chain
 * so that downstream transforms (coerceDate, coerceNumeric) receive `null`
 * instead of an "N/A" string they cannot parse.
 *
 * @param {*} value - The raw cell value
 * @returns {*} The original value, or `null` if it matched an N/A pattern
 *
 * @example
 * normalizeNA('N/A');    // null
 * normalizeNA('n/a');    // null
 * normalizeNA('NA');     // null
 * normalizeNA('na');     // null
 * normalizeNA('');       // '' (empty string is NOT treated as N/A)
 * normalizeNA(42);       // 42 (non-strings pass through)
 * normalizeNA('hello');  // 'hello'
 */
export function normalizeNA(value) {
  if (typeof value === 'string' && NA_RE.test(value.trim())) {
    return null;
  }
  return value;
}

/**
 * Compose multiple transform functions left-to-right, short-circuiting on null/undefined.
 *
 * This is the primary mechanism for building multi-step transform pipelines.
 * Each transform receives the output of the previous one. If any transform
 * returns `null` or `undefined`, the chain stops immediately and returns `null`.
 *
 * **Short-circuit behavior:** This is intentional -- once a value is determined
 * to be empty/unparseable (null), there is no point running further transforms.
 *
 * @param {...Function} transforms - Transform functions to compose. Each takes a
 *   single value and returns the transformed value (or null).
 * @returns {Function} A composed function `(value) => transformedValue` that
 *   applies all transforms left-to-right.
 *
 * @example <caption>Chain normalizeNA with coerceDate</caption>
 * import { composeTransforms, normalizeNA, coerceDate } from '@1823-partners/core';
 *
 * const toDate = composeTransforms(normalizeNA, coerceDate);
 * toDate('N/A');            // null (normalizeNA returns null, chain stops)
 * toDate('2023-01-01');     // '2023-01-01'
 * toDate(44927);            // '2023-01-01'
 *
 * @example <caption>Custom chain with preprocessing</caption>
 * const cleanAndParseNumber = composeTransforms(
 *   normalizeNA,                                    // N/A -> null
 *   (v) => typeof v === 'string' ? v.replace('%', '') : v,  // strip '%'
 *   coerceNumeric,                                  // parse to number
 * );
 * cleanAndParseNumber('3.5%');  // 3.5
 * cleanAndParseNumber('N/A');   // null
 */
export function composeTransforms(...transforms) {
  return function composed(value) {
    let current = value;
    for (const transform of transforms) {
      if (current === null || current === undefined) {
        return null;
      }
      current = transform(current);
    }
    return current;
  };
}

/**
 * Build a map of `{ fieldName: transformFunction }` from field definitions.
 *
 * This is the recommended way to create transforms for the upload pipeline.
 * It reads each field's `datatype` property and selects the appropriate
 * transform chain:
 *
 * | `datatype` | Transform chain |
 * |---|---|
 * | `'date'` | `composeTransforms(normalizeNA, coerceDate)` |
 * | `'numeric'` | `composeTransforms(normalizeNA, coerceNumeric)` |
 * | anything else | `normalizeNA` only |
 *
 * The `overrides` parameter lets you replace the auto-generated transform for
 * specific fields. This is useful for fields that need custom parsing logic
 * (e.g., a rating field that should be uppercased, or a percentage field that
 * needs the "%" stripped before numeric coercion).
 *
 * @param {Object<string, {datatype: string}>} fieldDefs - Field definitions keyed
 *   by field name. Each value must have at minimum a `datatype` property.
 *   Typically loaded from a JSON file like `creFields.json`.
 *   Example: `{ MATURITY_DATE: { datatype: 'date' }, YIELD: { datatype: 'numeric' } }`
 * @param {Object<string, Function>} [overrides={}] - Optional per-field transform
 *   overrides. If a field name appears in both `fieldDefs` and `overrides`, the
 *   override wins. Set a field to `null` to skip transforms for that field.
 * @returns {Object<string, Function>} Map of field name to transform function.
 *   Pass this as `fieldTransforms` to `createSheetProcessor` or as
 *   `fieldTransformOverrides` to `useUploadConfig`.
 *
 * @example <caption>Auto-generate from creFields.json</caption>
 * import { buildFieldTransforms } from '@1823-partners/core';
 * import creFields from '../data/creFields.json';
 *
 * const transforms = buildFieldTransforms(creFields);
 * // Result:
 * // {
 * //   CUSIP: normalizeNA,                                    // datatype: 'string'
 * //   LOAN_NAME: normalizeNA,                                // datatype: 'string'
 * //   SERVICER_NUMBER: composeTransforms(normalizeNA, coerceNumeric), // datatype: 'numeric'
 * //   MATURITY_DATE: composeTransforms(normalizeNA, coerceDate),      // datatype: 'date'
 * //   ... one entry per field in creFields
 * // }
 *
 * @example <caption>With overrides for custom fields</caption>
 * const transforms = buildFieldTransforms(creFields, {
 *   // Force RATING to uppercase
 *   RATING: (value) => value?.toString().toUpperCase().trim() || null,
 *   // Skip transform for a field that should stay raw
 *   NOTES: (value) => value,
 * });
 */
export function buildFieldTransforms(fieldDefs, overrides = {}) {
  const result = {};

  for (const [field, def] of Object.entries(fieldDefs)) {
    if (field in overrides) {
      result[field] = overrides[field];
      continue;
    }

    const { datatype } = def;

    if (datatype === 'date') {
      result[field] = composeTransforms(normalizeNA, coerceDate);
    } else if (datatype === 'numeric') {
      result[field] = composeTransforms(normalizeNA, coerceNumeric);
    } else {
      // string, boolean, or unknown -- normalizeNA only
      result[field] = normalizeNA;
    }
  }

  return result;
}
